import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import Clarity from '@microsoft/clarity';
import { environment } from '../../../../environments/environment';

/**
 * Microsoft Clarity Analytics Service
 * Provides SSR-safe wrapper for Microsoft Clarity integration
 * with environment-specific custom tags
 */
@Injectable({
  providedIn: 'root',
})
export class ClarityService {
  private platformId = inject(PLATFORM_ID);
  private isInitialized = false;

  /**
   * Initialize Microsoft Clarity with project ID and environment tag
   * Only initializes if:
   * - Running in browser (SSR-safe)
   * - Clarity project ID is configured in environment
   * - Not already initialized
   */
  initialize(): void {
    // Only run in browser environment
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Check if Clarity is configured for this environment
    const clarityProjectId = (environment as any).clarityProjectId;
    const clarityEnvironment = (environment as any).clarityEnvironment;

    if (!clarityProjectId) {
      console.log('Clarity: Not configured for this environment');
      return;
    }

    // Prevent double initialization
    if (this.isInitialized) {
      console.warn('Clarity: Already initialized');
      return;
    }

    try {
      // Initialize Clarity with project ID
      Clarity.init(clarityProjectId);

      // Set environment tag immediately after initialization
      if (clarityEnvironment) {
        Clarity.setTag('environment', clarityEnvironment);
      }

      this.isInitialized = true;
      console.log(
        `Clarity: Initialized successfully (Environment: ${clarityEnvironment})`
      );
    } catch (error) {
      console.error('Clarity: Initialization failed', error);
    }
  }

  /**
   * Set a custom tag in Clarity
   * @param key Tag key
   * @param value Tag value
   */
  setTag(key: string, value: string): void {
    if (!isPlatformBrowser(this.platformId) || !this.isInitialized) {
      return;
    }

    try {
      Clarity.setTag(key, value);
    } catch (error) {
      console.error('Clarity: Failed to set tag', error);
    }
  }

  /**
   * Identify a user in Clarity
   * @param userId User identifier
   * @param sessionId Optional session identifier
   * @param pageId Optional page identifier
   * @param friendlyName Optional friendly name
   */
  identify(
    userId: string,
    sessionId?: string,
    pageId?: string,
    friendlyName?: string
  ): void {
    if (!isPlatformBrowser(this.platformId) || !this.isInitialized) {
      return;
    }

    try {
      Clarity.identify(userId, sessionId, pageId, friendlyName);
    } catch (error) {
      console.error('Clarity: Failed to identify user', error);
    }
  }

  /**
   * Upgrade Clarity session to be stored permanently
   * @param reason Reason for upgrading the session
   */
  upgrade(reason: string): void {
    if (!isPlatformBrowser(this.platformId) || !this.isInitialized) {
      return;
    }

    try {
      Clarity.upgrade(reason);
    } catch (error) {
      console.error('Clarity: Failed to upgrade session', error);
    }
  }

  /**
   * Send a custom event to Clarity
   * @param eventName Name of the event
   */
  event(eventName: string): void {
    if (!isPlatformBrowser(this.platformId) || !this.isInitialized) {
      return;
    }

    try {
      Clarity.event(eventName);
    } catch (error) {
      console.error('Clarity: Failed to send event', error);
    }
  }

  /**
   * Check if Clarity is initialized
   */
  isReady(): boolean {
    return isPlatformBrowser(this.platformId) && this.isInitialized;
  }
}
